<template>
    <div class="columns">
        <div class="column">
            <div class="is-flex is-justify-content-center mb-5">
                <figure class="image asaas-logo">
                    <img src="https://static.martialmatch.com/images/help-center/25bdf5a2.jpg" />
                </figure>
            </div>

            <div>
                <div class="field">
                    <label class="label">{{ translate("pricing.config_name") }}:</label>
                    <div class="control">
                        <input type="text" class="input" v-model="configName" />
                    </div>
                    <p class="help">
                        {{ translate("pricing.config_name_description") }}
                    </p>
                </div>

                <div class="field">
                    <label class="label">{{ translate("pricing.p24_api_key") }}</label>
                    <div class="control">
                        <input type="text" class="input" v-model="apiKey" />
                    </div>
                </div>

                <div class="field is-grouped">
                    <div class="control">
                        <LoadingButton
                            @click="createConfiguration"
                            class="button is-primary"
                            :disabled="!newFormIsValid"
                            :is-loading="creatingNewInProgress"
                        >
                            <span class="icon">
                                <i class="fas fa-check"></i>
                            </span>
                            <span>{{ translate("common.save") }}</span>
                        </LoadingButton>
                    </div>

                    <div class="control">
                        <LoadingButton
                            class="button"
                            @click="checkConnection"
                            :is-loading="connectionCheckInProgress"
                            :disabled="!connectionCanBeChecked"
                        >
                            <span class="icon">
                                <i class="fas fa-plug"></i>
                            </span>
                            <span>{{ translate("pricing.check_connection") }}</span>
                        </LoadingButton>
                    </div>
                </div>

                <div v-if="connectionErrorResponse" class="connection-check-api-responses has-text-danger">
                    <p class="mb-1">{{ translate("common.status") }}: {{ connectionErrorResponse.statusCode }}</p>
                    <p>{{ connectionErrorResponse.body }}</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { translate } from "../../Common/translations";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import { computed, ref } from "vue";
    import paymentGatewayConfigApi, {
        AsaasProviderConfiguration,
        CheckConnectionResponse,
        ProviderEnum,
    } from "../../Api/paymentGatewayConfig";
    import { usePaymentGatewayConfigStore, usePricingSetupStore } from "../pricingSetupApp";
    import { errorToast, successToast } from "../../Common/toasts";
    import { handleError } from "../../Common/errorHandler";

    const configName = ref("");
    const apiKey = ref("");

    const creatingNewInProgress = ref(false);
    const newFormIsValid = computed(() => {
        return configName.value.length > 0 && apiKey.value.length > 0 && connectionCheck.value;
    });

    const paymentGatewayConfigStore = usePaymentGatewayConfigStore();

    function createConfiguration() {
        creatingNewInProgress.value = true;
        return paymentGatewayConfigApi
            .createConfig(store.userId, configName.value, ProviderEnum.Asaas, {
                apiKey: apiKey.value,
            } as AsaasProviderConfiguration)
            .then(createdConfig => {
                paymentGatewayConfigStore.chosenConfig = createdConfig;
                paymentGatewayConfigStore.view = "general_configuration";
            })
            .catch(handleError)
            .finally(() => {
                creatingNewInProgress.value = false;
            });
    }

    const connectionCheck = ref(false);
    const connectionCheckInProgress = ref(false);
    const connectionCanBeChecked = computed(() => {
        return apiKey.value.length > 0;
    });
    const store = usePricingSetupStore();
    const connectionErrorResponse = ref<CheckConnectionResponse["apiResponse"]>(null);

    function checkConnection() {
        connectionCheckInProgress.value = true;
        connectionErrorResponse.value = null;
        return paymentGatewayConfigApi
            .checkConnection<AsaasProviderConfiguration>(store.userId, {
                provider: ProviderEnum.Asaas,
                providerConfiguration: {
                    apiKey: apiKey.value,
                },
            })
            .then(res => {
                if (res.success) {
                    connectionCheck.value = true;
                    successToast(translate("pricing.check_connection.success"));
                } else {
                    connectionCheck.value = false;
                    connectionErrorResponse.value = res.apiResponse;
                    errorToast(translate("pricing.check_connection.fail"));
                }
            })
            .catch(handleError)
            .finally(() => {
                connectionCheckInProgress.value = false;
            });
    }
</script>

<style scoped>
    .image.asaas-logo {
        background-color: whitesmoke;
        width: 128px;
    }

    .connection-check-api-responses {
        background-color: white;
        font-family: Consolas, "Courier New", Courier, monospace;
        padding: 10px;
    }
</style>
